import type { Tender } from '../../../types/tender';

export interface FeedbackSubmission {
  Analysis: string[];
  Draft: string[];
  Review: string[];
  additionalComments: string;
}

export const displayDaysUntilDeadline = (
  daysUntilNumericOrError: number | string | null | undefined
) => {
  if (
    daysUntilNumericOrError === null ||
    daysUntilNumericOrError === undefined ||
    typeof daysUntilNumericOrError === 'string'
  ) {
    return daysUntilNumericOrError || 'N/A';
  }

  const diffDays = daysUntilNumericOrError;

  if (diffDays === 0) {
    return 'Today';
  } else if (diffDays === 1) {
    return '1 day';
  } else if (diffDays < 0) {
    return 'Closed';
  } else {
    return `${diffDays} days`;
  }
};

export const parseSubmissionDeadline = (deadlineStr: string | null | undefined): Date | null => {
  if (!deadlineStr) {
    return null;
  }
  const dateTimeRegex =
    /(\d{1,2})\/([A-Za-z]{3})\/(\d{4})\s*(\d{1,2}):(\d{2})\s*(AM|PM)\s*(?:\(UTC([+-]\d{2})\))?/;
  const dateTimeMatch = deadlineStr.match(dateTimeRegex);
  if (!dateTimeMatch) {
    return null;
  }

  const [, dayStr, monthStr, yearStr, hourStr, minuteStr, ampm, utcOffset] = dateTimeMatch;

  const monthMap = {
    Jan: 0,
    Feb: 1,
    Mar: 2,
    Apr: 3,
    May: 4,
    Jun: 5,
    Jul: 6,
    Aug: 7,
    Sep: 8,
    Oct: 9,
    Nov: 10,
    Dec: 11,
  };
  const month = monthMap[monthStr as keyof typeof monthMap];
  if (month === undefined) {
    return null;
  }

  let hour = parseInt(hourStr || '0', 10);
  if (ampm && ampm.toUpperCase() === 'PM' && hour !== 12) {
    hour += 12;
  } else if (ampm && ampm.toUpperCase() === 'AM' && hour === 12) {
    hour = 0;
  }

  const dateStr = `${yearStr}-${String(month + 1).padStart(2, '0')}-${String(dayStr).padStart(2, '0')}T${String(hour).padStart(2, '0')}:${minuteStr}:00`;

  try {
    if (utcOffset) {
      const formattedOffset = `${utcOffset}:00`;
      const isoString = `${dateStr}${formattedOffset}`;
      const dateObj = new Date(isoString);
      if (Number.isNaN(dateObj.getTime())) {
        return null;
      }
      return dateObj;
    } else {
      const dateObj = new Date(dateStr);
      if (Number.isNaN(dateObj.getTime())) {
        return null;
      }
      return dateObj;
    }
  } catch (_error) {
    return null;
  }
};

export const getDeadlineDate = (tender: Tender | null): Date | null => {
  if (!tender) {
    return null;
  }

  if (tender.deadlineDate) {
    const explicitDeadline = new Date(tender.deadlineDate);
    if (!Number.isNaN(explicitDeadline.getTime())) {
      return explicitDeadline;
    }
  }

  let deadline: Date | null | undefined;
  if (tender.submissionDeadline) {
    deadline = parseSubmissionDeadline(tender.submissionDeadline);
  }

  if (deadline && !Number.isNaN(deadline.getTime())) {
    return deadline;
  }

  return null;
};

export const generateICSData = (tender: Tender, deadlineDate: Date) => {
  if (!tender || !deadlineDate || Number.isNaN(deadlineDate.getTime())) {
    return null;
  }
  const toICSDateTime = (date: Date) => {
    return date.toISOString().replace(/-|:|\.\d{3}/g, '');
  };
  const now = new Date();
  const uid = `${tender.id || 'event'}-${toICSDateTime(now)}@bidlogiq.com`;
  const eventStart = deadlineDate;
  const eventEnd = new Date(eventStart.getTime() + 60 * 60 * 1000);
  const summary = `Tender Closing: ${tender.title || 'N/A'}`;
  const description =
    `Tender: ${tender.title || 'N/A'}\nTender ID: ${tender.tenderId || tender.opportunityID || tender.id || 'N/A'}\nClient: ${tender.tenderIssuer || 'N/A'}`.replace(
      /\n/g,
      '\\n'
    );
  const icsLines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//BIDLOGIQ//TenderReminder//EN',
    'CALSCALE:GREGORIAN',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${toICSDateTime(now)}`,
    `DTSTART:${toICSDateTime(eventStart)}`,
    `DTEND:${toICSDateTime(eventEnd)}`,
    `SUMMARY:${summary}`,
    `DESCRIPTION:${description}`,
    'BEGIN:VALARM',
    'ACTION:DISPLAY',
    'DESCRIPTION:REMINDER',
    'TRIGGER;RELATED=START:-P7D',
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR',
  ];
  return icsLines.join('\r\n');
};
